/**
 * Универсальный polling для async Suno-операций.
 *
 * Все music/* эндпоинты (кроме boost-style) возвращают `request_id`
 * и работают через ту же систему задач что и изображения. Этот хук
 * абстрагирует старт операции + опрос статуса + очистку при unmount.
 *
 * Пример:
 *   const { run, status, progress, result, error } = useSunoPolling()
 *   await run(() => generateLyrics({ prompt: '...' }))
 *   // status = 'idle' | 'running' | 'completed' | 'failed'
 *
 * НЕ путать с useGenerationFlow — тот делает полную генерацию
 * с сохранением в историю. Этот хук — тонкий старт + опрос для
 * sub-operations Suno (lyrics, wav, stem, cover, etc).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { type TaskStatusResponse } from '../../../api/images'
import { waitForTask } from '../utils/waitForTask'
import { translateSunoError } from '../utils/sunoErrors'
import { requestRefusalCheck } from '../../../api/refusals'

const POLL_INTERVAL_MS = 10_000

export type SunoPollingStatus = 'idle' | 'running' | 'completed' | 'failed'

interface StartResponse {
	ok: boolean
	request_id?: string
	error?: string
	// ─── Immediate-response поля (recovery на бэке через record-info) ───
	// Если бэк вытащил готовый результат без polling'а — возвращает его в
	// одном из этих полей. Hook распознаёт и сразу выдаёт synthetic completed
	// TaskStatusResponse без запуска polling цикла.
	wav_url?: string | null
	cover_urls?: string[] | null
	midi_data?: Record<string, unknown> | null
	// Синхронная операция уже завершена на бэке (напр. Mureka /lyrics —
	// текст готов сразу). Возвращаем completed мгновенно, без 10с-ожидания
	// первого опроса. Результат фронт дотягивает сам (getUserTracks).
	completed?: boolean
}

/** Собирает synthetic TaskStatusResponse из immediate-полей ответа.
 *  null — если в ответе нет ни одного immediate-поля. */
function synthesizeImmediate(
	startRes: StartResponse,
): TaskStatusResponse | null {
	const hasWav = !!startRes.wav_url
	const hasCovers = Array.isArray(startRes.cover_urls) && startRes.cover_urls.length > 0
	const hasMidi = !!startRes.midi_data
	if (!hasWav && !hasCovers && !hasMidi) return null

	const result_urls: string[] = []
	if (hasWav && startRes.wav_url) result_urls.push(startRes.wav_url)
	if (hasCovers && startRes.cover_urls) result_urls.push(...startRes.cover_urls)

	return {
		request_id: startRes.request_id || 'immediate',
		status: 'completed',
		type: 'audio',
		model: '',
		result_url: result_urls[0],
		result_urls: result_urls.length > 0 ? result_urls : undefined,
		response_data: hasMidi ? startRes.midi_data ?? undefined : undefined,
	}
}

interface UseSunoPollingResult {
	status: SunoPollingStatus
	result: TaskStatusResponse | null
	error: string | null
	/** Запустить операцию. starter должна вернуть стандартный MusicGenerateResponse. */
	run: (starter: () => Promise<StartResponse>) => Promise<TaskStatusResponse | null>
	reset: () => void
}

export function useSunoPolling(): UseSunoPollingResult {
	const [status, setStatus] = useState<SunoPollingStatus>('idle')
	const [result, setResult] = useState<TaskStatusResponse | null>(null)
	const [error, setError] = useState<string | null>(null)
	// Номер текущего запуска: reset и новый run его меняют, и прежний опрос узнаёт, что он лишний, — свой ответ
	// поверх нового не пишет. Общий флаг «стоп» новый run снимал посреди паузы старого опроса, и старый оживал; раньше
	// его гасил потолок в 5 минут, теперь опрос ждёт ответа сервера сколько нужно.
	const runRef = useRef(0)
	const unmountedRef = useRef(false)

	useEffect(() => {
		return () => {
			unmountedRef.current = true
		}
	}, [])

	const reset = useCallback(() => {
		runRef.current += 1
		setStatus('idle')
		setResult(null)
		setError(null)
	}, [])

	const run = useCallback(
		async (starter: () => Promise<StartResponse>): Promise<TaskStatusResponse | null> => {
			const my = ++runRef.current
			const stale = () => unmountedRef.current || runRef.current !== my
			setStatus('running')
			setResult(null)
			setError(null)

			try {
				const startRes = await starter()
				if (stale()) return null

				// Immediate result (recovery на бэке) — сразу возвращаем,
				// не запуская polling.
				if (startRes.ok) {
					const immediate = synthesizeImmediate(startRes)
					if (immediate) {
						setStatus('completed')
						setResult(immediate)
						return immediate
					}
					// Синхронная операция (Mureka /lyrics): completed без
					// result_url'ов — результат фронт дотянет сам. Без 10с-ждать.
					if (startRes.completed) {
						const done: TaskStatusResponse = {
							request_id: startRes.request_id || 'immediate',
							status: 'completed',
							type: 'audio',
							model: '',
						}
						setStatus('completed')
						setResult(done)
						return done
					}
				}

				if (!startRes.ok || !startRes.request_id) {
					setError(translateSunoError(startRes.error) || 'Ошибка запуска операции')
					setStatus('failed')
					return null
				}

				// Ждём ответа сервера без потолка (waitForTask): прежние 5 минут объявляли «Превышено время
				// ожидания», пока Suno досчитывал.
				const s = await waitForTask(startRes.request_id, {
					minDelayMs: POLL_INTERVAL_MS,
					shouldStop: stale,
				})
				if (!s || stale()) return null
				if (s.status === 'completed') {
					setStatus('completed')
					setResult(s)
					return s
				}
				// Отказ за нарушение правил — окно покажет общий хост (RefusalNoticeHost), если сервер его записал.
				requestRefusalCheck()
				setError(translateSunoError(s.error_message_ru || s.error_message) || 'Операция не удалась')
				setStatus('failed')
				return null
			} catch (err) {
				if (stale()) return null
				const message = err instanceof Error ? err.message : 'Неизвестная ошибка'
				setError(message)
				setStatus('failed')
				return null
			}
		},
		[],
	)

	return { status, result, error, run, reset }
}
